import { bindServices } from '@/services/api';

export const TAB_KEYS = {
  PROFILE: 'profile',
  LIMITS: 'limits',
  PERIODS: 'periods',
  GRANTS: 'grants',
};

const api = bindServices('aiBilling');

const namespace = 'aiBilling';
const PROFILE_CURRENT_ROUTE = 'billing/ai/profile/current';
const PROFILE_ROUTE = 'billing/ai/profile';
const ALERTS_ROUTE = 'billing/ai/budget/alerts';

const createResourceState = () => ({
  loading: false,
  error: null,
  loaded: false,
});

const unwrapAlertList = (response) => {
  if (Array.isArray(response)) return response;
  return response?.items ?? response?.data ?? response?.alerts ?? [];
};

const unwrapProfileResponse = (response) => {
  if (!response || typeof response !== 'object') return null;
  return response.profile ?? response.data?.profile ?? response.data ?? response;
};

const initialState = {
  activeTab: TAB_KEYS.PROFILE,
  canEdit: false,
  profile: {
    ...createResourceState(),
    data: null,
    saving: false,
  },
  alerts: {
    ...createResourceState(),
    list: [],
    saving: false,
    saveSuccess: false,
  },
};

const AiBillingModel = {
  namespace,
  state: initialState,
  effects: {
    *init({ payload }, { put }) {
      yield put({ type: 'setCanEdit', payload: payload?.canEdit ?? false });
      yield put({ type: 'updateState', payload: { activeTab: TAB_KEYS.PROFILE } });
      yield put({ type: 'fetchProfile', payload: { force: true } });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *setActiveTab({ payload }, { put }) {
      yield put({ type: 'updateState', payload: { activeTab: payload } });

      if (payload === TAB_KEYS.LIMITS) {
        yield put({ type: 'fetchAlerts' });
      }
    },

    *fetchAlerts({ payload }, { call, put, select }) {
      const { loaded, loading } = yield select((state) => state[namespace].alerts);
      if (!payload?.force && (loaded || loading)) return;

      yield put({ type: 'setAlertsState', payload: { loading: true, error: null } });
      try {
        const response = yield call(api.getAll, {
          route: ALERTS_ROUTE,
          readPriority: true,
        });
        const list = unwrapAlertList(response);
        yield put({
          type: 'setAlertsState',
          payload: { loading: false, loaded: true, list, error: null },
        });
      } catch (error) {
        yield put({
          type: 'setAlertsState',
          payload: { loading: false, loaded: false, error: error?.message || 'FETCH_ERROR' },
        });
      }
    },

    *patchProfile({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      const profileData = yield select((state) => state[namespace].profile.data);
      const id = profileData?.id ?? profileData?.Id;

      if (!canEdit || !id || !payload || !Object.keys(payload).length) return;

      yield put({ type: 'setProfileState', payload: { saving: true } });
      try {
        const response = yield call(api.updateById, {
          route: PROFILE_ROUTE,
          id,
          data: payload,
        });
        const data = unwrapProfileResponse(response) ?? { ...profileData, ...payload };

        yield put({
          type: 'setProfileState',
          payload: { saving: false, data, loaded: true, error: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'setProfileState', payload: { saving: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },

    *fetchProfile({ payload }, { call, put, select }) {
      const { loading, loaded, data } = yield select((state) => state[namespace].profile);
      if (!payload?.force && (loading || (loaded && data))) return;

      yield put({ type: 'setProfileState', payload: { loading: true, error: null } });
      try {
        const response = yield call(api.getAll, {
          route: PROFILE_CURRENT_ROUTE,
          readPriority: true,
        });
        const data = unwrapProfileResponse(response);
        yield put({
          type: 'setProfileState',
          payload: { loading: false, loaded: true, data, error: null },
        });
      } catch (error) {
        yield put({
          type: 'setProfileState',
          payload: { loading: false, loaded: false, error: error?.message || 'FETCH_ERROR' },
        });
      } finally {
        yield put({ type: 'setProfileState', payload: { loading: false } });
      }
    },

    *createAlert({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      yield put({ type: 'setAlertsState', payload: { saving: true, saveSuccess: false } });
      try {
        yield call(api.create, { route: ALERTS_ROUTE, data: payload });
        yield put({ type: 'fetchAlerts', payload: { force: true } });
        yield put({
          type: 'setAlertsState',
          payload: { saving: false, saveSuccess: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({
          type: 'setAlertsState',
          payload: { saving: false, saveSuccess: false },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },

    *updateAlert({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      yield put({ type: 'setAlertsState', payload: { saving: true, saveSuccess: false } });
      try {
        yield call(api.update, { route: ALERTS_ROUTE, data: payload });
        yield put({ type: 'fetchAlerts', payload: { force: true } });
        yield put({
          type: 'setAlertsState',
          payload: { saving: false, saveSuccess: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({
          type: 'setAlertsState',
          payload: { saving: false, saveSuccess: false },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },

    *deleteAlert({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      const id = payload?.id;
      if (!canEdit || !id) return;

      yield put({
        type: 'global/modalConfirm',
        payload: { state: false, id: null },
      });
      yield put({ type: 'setAlertsState', payload: { saving: true } });
      try {
        yield call(api.remove, { route: ALERTS_ROUTE, id });
        yield put({ type: 'fetchAlerts', payload: { force: true } });
        yield put({ type: 'setAlertsState', payload: { saving: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'setAlertsState', payload: { saving: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },

    *activateAlert({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit || !payload?.id) return;

      try {
        yield call(api.activate, {
          route: ALERTS_ROUTE,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'alertFieldUpdate', payload });
      } catch (e) {
        /* same as positions fetchActivate */
      }
    },

    *patchAlertSendToOrganizationOwners({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit || !payload?.id) return;

      try {
        yield call(api.updateById, {
          route: ALERTS_ROUTE,
          id: payload.id,
          data: { sendToOrganizationOwners: payload.sendToOrganizationOwners },
        });
        yield put({
          type: 'alertFieldUpdate',
          payload: {
            id: payload.id,
            field: 'sendToOrganizationOwners',
            value: payload.sendToOrganizationOwners,
          },
        });
      } catch (error) {
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },
  },
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    setCanEdit(state, { payload }) {
      return { ...state, canEdit: payload };
    },
    setProfileState(state, { payload }) {
      return { ...state, profile: { ...state.profile, ...payload } };
    },
    setAlertsState(state, { payload }) {
      return { ...state, alerts: { ...state.alerts, ...payload } };
    },
    alertFieldUpdate(state, { payload }) {
      const { id, field, value, state: isActive } = payload;
      const nextValue = field != null ? value : isActive;

      return {
        ...state,
        alerts: {
          ...state.alerts,
          list: state.alerts.list.map((item) => {
            if (item.id !== id) return item;
            if (field != null) return { ...item, [field]: nextValue };
            return { ...item, isActive: nextValue };
          }),
        },
      };
    },

    reset() {
      return {
        activeTab: TAB_KEYS.PROFILE,
        canEdit: false,
        profile: {
          ...createResourceState(),
          data: null,
          saving: false,
        },
        alerts: {
          ...createResourceState(),
          list: [],
          saving: false,
          saveSuccess: false,
        },
      };
    },
  },
};

export { namespace };
export default AiBillingModel;
